/**
 * Read-only User 360 (OD-65): everything about the person, in full — identity, mobile, e-mail, cards
 * and IBANs, balances (server strings), every deposit / withdrawal / conversion, sessions, security
 * events and support history. Nothing here changes anything; the footer says where changes happen.
 */
import { cx, formatAmount, formatJalali, formatRelative, useToast } from '@hopex/ui';
import {
  ArrowRight,
  Banknote,
  Copy,
  CreditCard,
  History,
  Landmark,
  LifeBuoy,
  Settings2,
  ShieldCheck,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import {
  ACCOUNT_FA,
  KIND_FA,
  KYC_FA,
  SECURITY_EVENT_FA,
  STATUS_FA,
  TX_FA,
  TX_STATUS_FA,
} from '../lib/labels.ts';
import { navigate } from '../lib/platform.ts';
import type { User360 } from '../lib/types.ts';
import { UserMoneyPanel } from './OpsUser.tsx';
import { TypingDots } from './parts.tsx';

type Tab = 'SUMMARY' | 'IDENTITY' | 'BANKS' | 'ACTIVITY' | 'SECURITY' | 'SUPPORT';
const TABS: { id: Tab; label: string; icon: typeof Wallet }[] = [
  { id: 'SUMMARY', label: 'خلاصه', icon: Wallet },
  { id: 'IDENTITY', label: 'هویت', icon: UserRound },
  { id: 'BANKS', label: 'بانکی', icon: CreditCard },
  { id: 'ACTIVITY', label: 'تراکنش‌ها', icon: History },
  { id: 'SECURITY', label: 'امنیت', icon: ShieldCheck },
  { id: 'SUPPORT', label: 'پشتیبانی', icon: LifeBuoy },
];
const unit = (asset: string) => (asset === 'IRT' ? 'TOMAN' : asset);
const amt = (v: string | null, asset: string) => (
  <bdi dir="ltr" className="hx-num">
    {formatAmount(v, unit(asset))}
  </bdi>
);

export function User360Panel({
  userId,
  onClose,
  canOps = false,
}: {
  userId: string;
  onClose: () => void;
  /** OD-71: the agent has at least one operations group → «عملیات» opens the editable file. */
  canOps?: boolean;
}) {
  const [u, setU] = useState<User360 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('SUMMARY');
  // The parent keys this panel by user id, so a new person starts from a fresh state.
  useEffect(() => {
    api
      .get<User360>(`/users/${userId}/360`)
      .then(setU)
      .catch((e: unknown) => setError(errorMessage(e)));
  }, [userId]);

  const name = u
    ? [u.identity.firstName, u.identity.lastName].filter(Boolean).join(' ') ||
      u.user.displayName ||
      'بدون نام'
    : '';
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col" data-user360={userId}>
      <header
        className="relative shrink-0 overflow-hidden border-b border-line bg-canvas-deep px-4 pb-4 pt-[calc(env(safe-area-inset-top)+12px)] lg:pt-4"
        data-theme-scope="dark"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_120%_at_0%_0%,color-mix(in_srgb,var(--hx-accent)_24%,transparent),transparent_65%)]"
        />
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="بستن پرونده"
            className="grid size-10 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2"
            data-360-close
          >
            <span className="lg:hidden">
              <ArrowRight aria-hidden className="size-5" />
            </span>
            <span className="hidden lg:inline">
              <X aria-hidden className="size-5" />
            </span>
          </button>
          <p className="m-0 flex-1 text-[13px] font-bold text-fg-muted">
            پرونده‌ی کامل کاربر{canOps ? '' : ' · فقط مشاهده'}
          </p>
          {canOps && (
            <button
              type="button"
              onClick={() => navigate(`/ops/u/${userId}`)}
              className="hx-press inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full bg-accent px-4 text-[13px] font-extrabold text-on-accent shadow-cta"
              data-360-ops
            >
              <Settings2 aria-hidden className="size-4" />
              عملیات
            </button>
          )}
        </div>
        {u && (
          <div className="dk-enter relative mt-3 flex items-center gap-3">
            <span className="grid size-14 shrink-0 place-items-center rounded-full bg-accent-tint text-[20px] font-extrabold text-accent-text ring-2 ring-[color-mix(in_srgb,var(--hx-accent)_50%,transparent)]">
              {name.trim()[0] ?? '?'}
            </span>
            <div className="min-w-0">
              <p className="m-0 truncate text-[18px] font-extrabold text-fg">{name}</p>
              <p className="m-0 mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-fg-muted">
                <bdi dir="ltr" className="font-mono">
                  {u.user.publicId}
                </bdi>
                <span className="rounded-full bg-accent-tint px-2 py-0.5 text-[11px] font-bold text-accent-text">
                  {KYC_FA[u.effectiveLevel]}
                </span>
                <span
                  className={cx(
                    'rounded-full px-2 py-0.5 text-[11px] font-bold',
                    u.user.status === 'ACTIVE'
                      ? 'bg-positive-tint text-positive'
                      : 'bg-negative-tint text-negative',
                  )}
                >
                  {ACCOUNT_FA[u.user.status] ?? u.user.status}
                </span>
              </p>
            </div>
          </div>
        )}
      </header>
      {!u ? (
        <div className="grid flex-1 place-items-center p-6">
          {error ? (
            <p role="alert" className="m-0 text-center text-[13px] text-negative">
              {error}
            </p>
          ) : (
            <TypingDots label="در حال خواندن پرونده…" />
          )}
        </div>
      ) : (
        <>
          <nav
            className="flex shrink-0 gap-1 overflow-x-auto border-b border-line px-2 py-2 [scrollbar-width:none]"
            aria-label="بخش‌های پرونده"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={tab === t.id}
                onClick={() => setTab(t.id)}
                className={cx(
                  'inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 text-[12.5px] font-bold',
                  tab === t.id
                    ? 'bg-accent text-on-accent'
                    : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                )}
                data-360-tab={t.id}
              >
                <t.icon aria-hidden className="size-4" />
                {t.label}
              </button>
            ))}
          </nav>
          <div
            key={tab}
            className="dk-enter min-h-0 flex-1 overflow-y-auto p-3 pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+16px)]"
          >
            {/* Owner 2026-10-17: the person's deposits / withdrawals with approve-reject, right here. */}
            {canOps && (tab === 'SUMMARY' || tab === 'ACTIVITY') && (
              <div className="mb-3">
                <UserMoneyPanel userId={userId} />
              </div>
            )}
            {tab === 'SUMMARY' && <Summary u={u} />}
            {tab === 'IDENTITY' && <Identity u={u} />}
            {tab === 'BANKS' && <Banks u={u} />}
            {tab === 'ACTIVITY' && <Activity u={u} />}
            {tab === 'SECURITY' && <Security u={u} />}
            {tab === 'SUPPORT' && <Support u={u} />}
            {!canOps && (
              <p className="m-0 mt-4 rounded-[14px] border border-dashed border-line px-3 py-2.5 text-center text-[11.5px] leading-6 text-fg-subtle">
                این پرونده فقط برای مشاهده است. تغییر حساب، احراز یا تراکنش‌ها با دسترسی «عملیات»
                انجام می‌شود.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Card({
  title,
  children,
  icon: Icon,
}: {
  title: string;
  children: ReactNode;
  icon?: typeof Wallet;
}) {
  return (
    <section className="hx-card mb-3 rounded-[20px] border border-line bg-surface-1 p-3.5">
      <h3 className="m-0 mb-2 flex items-center gap-1.5 text-[13px] font-extrabold">
        {Icon && <Icon aria-hidden className="size-4 text-accent-text" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function Row({
  k,
  v,
  copy,
  mono,
}: {
  k: string;
  v: ReactNode;
  copy?: string | null;
  mono?: boolean;
}) {
  const toast = useToast();
  return (
    <div className="flex min-h-10 items-center gap-2 border-b border-line py-1.5 last:border-b-0">
      <span className="w-28 shrink-0 text-[12px] text-fg-muted">{k}</span>
      <span
        className={cx(
          'min-w-0 flex-1 text-[13px] font-semibold [overflow-wrap:anywhere]',
          mono && 'font-mono',
        )}
      >
        {v ?? '—'}
      </span>
      {copy && (
        <button
          type="button"
          aria-label={`کپی ${k}`}
          onClick={() => {
            void navigator.clipboard
              ?.writeText(copy)
              .then(() => toast.show({ tone: 'success', title: `${k} کپی شد` }))
              .catch(() => undefined);
          }}
          className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-full text-fg-subtle hover:bg-surface-2 hover:text-fg"
        >
          <Copy aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}

const ltr = (v: string | null) =>
  v ? (
    <bdi dir="ltr" className="hx-num">
      {v}
    </bdi>
  ) : (
    '—'
  );
const local = (p: string | null) => (p ? p.replace(/^\+98/, '0') : null);

function Summary({ u }: { u: User360 }) {
  const nonZero = u.balances.filter((b) => b.total !== '0' && !/^0(\.0+)?$/.test(b.total));
  return (
    <>
      <Card title="دارایی‌ها" icon={Wallet}>
        {nonZero.length === 0 ? (
          <p className="m-0 text-[12.5px] text-fg-muted">موجودی ندارد (۰ در دفتر کل).</p>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {nonZero.map((b) => (
              <li
                key={b.asset}
                className="flex items-center gap-2 border-b border-line py-2 last:border-b-0"
              >
                <span className="w-20 shrink-0 text-[12.5px] font-bold">{b.nameFa}</span>
                <span className="flex-1 text-end text-[13px] font-extrabold">
                  {amt(b.total, b.asset)}
                </span>
                {b.reserved !== '0' && !/^0(\.0+)?$/.test(b.reserved) && (
                  <span className="text-[11px] text-pending">رزرو: {amt(b.reserved, b.asset)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="شناسه‌ها" icon={UserRound}>
        <Row k="موبایل" v={ltr(local(u.identity.phone))} copy={local(u.identity.phone)} />
        <Row k="کد ملی" v={ltr(u.identity.nationalId)} copy={u.identity.nationalId} />
        <Row k="شناسه هوپکس" v={ltr(u.user.publicId)} copy={u.user.publicId} />
        <Row k="عضویت" v={formatJalali(u.user.createdAt, { time: false })} />
        <Row k="آخرین فعالیت" v={formatRelative(u.user.lastActiveAt)} />
      </Card>
      <Card title="آخرین کارها" icon={History}>
        <Timeline items={u.timeline.slice(0, 8)} />
      </Card>
    </>
  );
}

function Identity({ u }: { u: User360 }) {
  return (
    <>
      <Card title="هویت (احراز سطح ۱)" icon={UserRound}>
        <Row k="نام" v={u.identity.firstName} copy={u.identity.firstName} />
        <Row k="نام خانوادگی" v={u.identity.lastName} copy={u.identity.lastName} />
        <Row k="نام پدر" v={u.identity.fatherName} />
        <Row k="کد ملی" v={ltr(u.identity.nationalId)} copy={u.identity.nationalId} />
        <Row
          k="تاریخ تولد"
          v={
            u.identity.birthDate
              ? formatJalali(`${u.identity.birthDate}T12:00:00Z`, { time: false })
              : null
          }
        />
        <Row k="موبایل" v={ltr(local(u.identity.phone))} copy={local(u.identity.phone)} />
        <Row
          k="منبع موبایل"
          v={
            u.identity.phoneSource === 'ADMIN'
              ? 'ثبت مدیر'
              : u.identity.phoneSource
                ? 'تأیید پیامکی'
                : null
          }
        />
        <Row k="ایمیل" v={ltr(u.identity.email)} copy={u.identity.email} />
      </Card>
      <Card title="وضعیت احراز" icon={ShieldCheck}>
        <Row k="سطح مؤثر" v={KYC_FA[u.effectiveLevel]} />
        <Row k="احراز سطح ۱" v={u.kyc ? (KYC_FA[u.kyc.status] ?? u.kyc.status) : 'شروع نشده'} />
        {u.kyc?.reason && <Row k="دلیل" v={u.kyc.reason} />}
        <Row k="تأیید سطح ۱" v={formatJalali(u.kyc?.verifiedAt)} />
        <Row k="احراز سطح ۲" v={KYC_FA[u.kyc2.status] ?? u.kyc2.status} />
        {u.kyc2.reason && <Row k="دلیل" v={u.kyc2.reason} />}
        {u.restrictions
          .filter((r) => !r.liftedAt)
          .map((r) => (
            <Row key={r.id} k="محدودیت فعال" v={r.capabilities.join('، ') || r.kind} />
          ))}
      </Card>
    </>
  );
}

function Banks({ u }: { u: User360 }) {
  if (!u.banks.length)
    return (
      <p className="m-0 mt-6 text-center text-[13px] text-fg-muted">کارت یا شبایی ثبت نکرده است.</p>
    );
  return (
    <ul className="dk-stagger m-0 flex list-none flex-col gap-2.5 p-0">
      {u.banks.map((b) => (
        <li
          key={b.id}
          className="relative overflow-hidden rounded-[20px] border border-line bg-canvas-deep p-4 text-fg shadow-card hx-frame"
          data-theme-scope="dark"
        >
          <div className="flex items-center gap-2">
            {b.kind === 'CARD' ? (
              <CreditCard aria-hidden className="size-5 text-accent-text" />
            ) : (
              <Landmark aria-hidden className="size-5 text-accent-text" />
            )}
            <span className="text-[12.5px] font-bold">
              {b.bank ?? (b.kind === 'CARD' ? 'کارت بانکی' : 'شبا')}
            </span>
            <span
              className={cx(
                'ms-auto rounded-full px-2 py-0.5 text-[10.5px] font-bold',
                b.ownershipStatus === 'VERIFIED'
                  ? 'bg-positive-tint text-positive'
                  : 'bg-surface-3 text-fg-muted',
              )}
            >
              {b.ownershipStatus === 'VERIFIED' ? 'مالکیت تأییدشده' : b.ownershipStatus}
            </span>
          </div>
          <Row
            k={b.kind === 'CARD' ? 'شماره کارت' : 'شبا'}
            v={ltr(
              b.value
                ? b.kind === 'CARD'
                  ? b.value.replace(/(\d{4})(?=\d)/g, '$1 ')
                  : b.value
                : b.masked,
            )}
            copy={b.value}
            mono
          />
          <p className="m-0 mt-1 text-[11px] text-fg-muted">
            ثبت: {formatJalali(b.createdAt, { time: false })} {b.active ? '' : '· غیرفعال'}
          </p>
        </li>
      ))}
    </ul>
  );
}

function Timeline({ items }: { items: User360['timeline'] }) {
  if (!items.length)
    return <p className="m-0 text-[12.5px] text-fg-muted">هنوز کاری ثبت نشده است.</p>;
  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {items.map((x, i) => (
        <li
          key={`${x.kind}-${x.ref}-${String(i)}`}
          className="flex items-start gap-2.5 border-b border-line py-2 last:border-b-0"
        >
          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
          <div className="min-w-0 flex-1">
            <p className="m-0 flex items-center gap-1.5 text-[12.5px] font-bold">
              {TX_FA[x.kind] ?? x.kind}
              <span className="rounded-full bg-surface-2 px-1.5 text-[10.5px] font-semibold text-fg-muted">
                {x.kind === 'SECURITY'
                  ? (SECURITY_EVENT_FA[x.ref] ?? x.ref)
                  : (TX_STATUS_FA[x.status] ?? x.status)}
              </span>
            </p>
            <p className="m-0 text-[11px] text-fg-subtle">
              {x.kind !== 'SECURITY' && (
                <bdi dir="ltr" className="font-mono">
                  {x.ref}
                </bdi>
              )}{' '}
              · {formatJalali(x.at)}
            </p>
          </div>
          {x.amount && x.asset && (
            <span className="shrink-0 text-[12.5px] font-extrabold">{amt(x.amount, x.asset)}</span>
          )}
        </li>
      ))}
    </ol>
  );
}

function Activity({ u }: { u: User360 }) {
  return (
    <>
      <Card title="واریز تومان" icon={Banknote}>
        {u.tomanDeposits.length === 0 ? (
          <Empty />
        ) : (
          u.tomanDeposits.map((d) => (
            <Tx
              key={d.id}
              ref_={d.ref}
              status={d.status}
              at={d.at}
              amount={amt(d.final ?? d.declared, 'IRT')}
              extra={d.rejectReason}
            />
          ))
        )}
      </Card>
      <Card title="برداشت تومان" icon={Banknote}>
        {u.tomanWithdrawals.length === 0 ? (
          <Empty />
        ) : (
          u.tomanWithdrawals.map((w) => (
            <Tx
              key={w.id}
              ref_={w.ref}
              status={w.status}
              at={w.at}
              amount={amt(w.amount, 'IRT')}
              extra={
                [
                  w.destination,
                  w.trackingRef ? `پیگیری ${w.trackingRef}` : null,
                  w.holdReason,
                  w.rejectReason,
                ]
                  .filter(Boolean)
                  .join(' · ') || null
              }
            />
          ))
        )}
      </Card>
      <Card title="واریز رمزارز" icon={Wallet}>
        {u.cryptoDeposits.length === 0 ? (
          <Empty />
        ) : (
          u.cryptoDeposits.map((d) => (
            <Tx
              key={d.id}
              ref_={d.ref}
              status={d.status}
              at={d.at}
              amount={amt(d.amount, d.asset)}
              extra={d.txid ? `TXID ${d.txid}` : d.network}
            />
          ))
        )}
      </Card>
      <Card title="برداشت رمزارز" icon={Wallet}>
        {u.cryptoWithdrawals.length === 0 ? (
          <Empty />
        ) : (
          u.cryptoWithdrawals.map((w) => (
            <Tx
              key={w.id}
              ref_={w.ref}
              status={w.status}
              at={w.at}
              amount={amt(w.amount, w.asset)}
              extra={`${w.network} · ${w.address}${w.txid ? ` · TXID ${w.txid}` : ''}`}
            />
          ))
        )}
      </Card>
      <Card title="تبدیل‌ها" icon={History}>
        {u.conversions.length === 0 ? (
          <Empty />
        ) : (
          u.conversions.map((c) => (
            <Tx
              key={c.id}
              ref_={c.ref}
              status={c.status}
              at={c.at}
              amount={
                <>
                  {amt(c.from.amount, c.from.asset)} ← {amt(c.to.amount, c.to.asset)}
                </>
              }
              extra={null}
            />
          ))
        )}
      </Card>
    </>
  );
}

const Empty = () => <p className="m-0 text-[12px] text-fg-muted">موردی نیست.</p>;

function Tx({
  ref_,
  status,
  at,
  amount,
  extra,
}: {
  ref_: string;
  status: string;
  at: string;
  amount: ReactNode;
  extra: string | null;
}) {
  return (
    <div className="border-b border-line py-2 last:border-b-0">
      <div className="flex items-center gap-2">
        <bdi dir="ltr" className="font-mono text-[11.5px] text-fg-muted">
          {ref_}
        </bdi>
        <span className="rounded-full bg-surface-2 px-1.5 text-[10.5px] font-bold text-fg-muted">
          {TX_STATUS_FA[status] ?? status}
        </span>
        <span className="ms-auto text-[12.5px] font-extrabold">{amount}</span>
      </div>
      <p className="m-0 mt-0.5 text-[11px] text-fg-subtle [overflow-wrap:anywhere]">
        {formatJalali(at)}
        {extra ? ` · ${extra}` : ''}
      </p>
    </div>
  );
}

function Security({ u }: { u: User360 }) {
  return (
    <>
      <Card title="امنیت حساب" icon={ShieldCheck}>
        <Row k="ورود دومرحله‌ای" v={u.security.totpEnabled ? 'فعال' : 'غیرفعال'} />
        <Row k="آخرین ورود" v={formatJalali(u.security.lastLoginAt)} />
        <Row k="تغییر رمز" v={formatJalali(u.security.passwordChangedAt)} />
      </Card>
      <Card title="نشست‌ها" icon={History}>
        {u.sessions.length === 0 ? (
          <Empty />
        ) : (
          u.sessions.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-2 border-b border-line py-2 text-[12px] last:border-b-0"
            >
              <span
                className={cx('size-2 rounded-full', s.revokedAt ? 'bg-fg-subtle' : 'bg-positive')}
              />
              <span className="flex-1">{s.device ?? 'دستگاه'}</span>
              <span className="text-fg-subtle">{formatRelative(s.lastSeenAt)}</span>
            </div>
          ))
        )}
      </Card>
      <Card title="رویدادهای امنیتی" icon={ShieldCheck}>
        {u.securityEvents.length === 0 ? (
          <Empty />
        ) : (
          u.securityEvents.slice(0, 30).map((e) => (
            <div
              key={e.id}
              className="flex items-center gap-2 border-b border-line py-2 text-[12px] last:border-b-0"
            >
              <span
                className={cx(
                  'size-2 rounded-full',
                  e.severity === 'CRITICAL'
                    ? 'bg-negative'
                    : e.severity === 'WARNING'
                      ? 'bg-pending'
                      : 'bg-fg-subtle',
                )}
              />
              <span className="flex-1">{SECURITY_EVENT_FA[e.type] ?? e.type}</span>
              <span className="text-fg-subtle">{formatJalali(e.at)}</span>
            </div>
          ))
        )}
      </Card>
    </>
  );
}

function Support({ u }: { u: User360 }) {
  if (!u.support.length)
    return <p className="m-0 mt-6 text-center text-[13px] text-fg-muted">گفتگوی دیگری ندارد.</p>;
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {u.support.map((s) => (
        <li key={s.id}>
          <a
            href={`/t/${s.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/t/${s.id}`);
            }}
            className="hx-press flex flex-col gap-1 rounded-[16px] border border-line bg-surface-1 p-3 hover:border-accent"
          >
            <span className="flex items-center gap-2 text-[12.5px] font-bold">
              {KIND_FA[s.kind]}
              <span className="ms-auto rounded-full bg-surface-2 px-1.5 text-[10.5px] text-fg-muted">
                {STATUS_FA[s.status] ?? s.status}
              </span>
            </span>
            <span className="truncate text-[12px] text-fg-muted">{s.subject}</span>
            <span className="text-[11px] text-fg-subtle">
              <bdi dir="ltr" className="font-mono">
                {s.ref}
              </bdi>{' '}
              · {formatJalali(s.at)}
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}
